/**
 * Presents the browser application picker, including bounded loading, retry,
 * filtering, and replacement of the selector tab with the chosen application.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {Actions, type ILayoutApi, type Model} from 'flexlayout-react';
import {type QepComponent} from '@qep/preloads/src/preloads/QEP/containers/Browser/communication/types';
import {getContainer} from '@qep/preloads/src/preloads/QEP/utils/index';
import {toTabNode} from '../../utils/utils.js';
import './component-selector.scss';

type Props = {
  layout: React.MutableRefObject<Pick<ILayoutApi, 'addTabToActiveTabSet' | 'addTabToTabSet'> | undefined>;
  model: React.MutableRefObject<Model | undefined>;
  tabId: string;
};

type AbortableAppClient = {
  getApplications: (signal?: AbortSignal) => Promise<unknown>;
};

const appLoadTimeoutMs = 10_000;

const accentPalette = [
  '#4652c2',
  '#2f9e7e',
  '#c2682f',
  '#8b46c2',
  '#2f8bc2',
  '#c24676',
  '#5fa030',
  '#c2a02f',
];

/** Produces a stable accent color from an application's name. */
function getAccent(name: string): string {
  let hash = 0;
  for (const character of name) {
    const code = character.codePointAt(0) ?? 0;
    hash = ((hash * 31) + code) % 2_147_483_647;
  }

  return accentPalette[hash % accentPalette.length];
}

/** Produces a compact label for an application's icon. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  const first = parts[0][0] ?? '';
  const last = parts.at(-1)?.[0] ?? '';
  return (first + last).toUpperCase();
}

/**
 * Loads the available applications and lets the user open one in place of the
 * selector tab, while exposing a retry path for transient service failures.
 */
export function ComponentSelector({
  layout,
  model,
  tabId,
}: Props) {
  const [apps, setApps] = useState<QepComponent[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const searchInput = useRef<HTMLInputElement>(null);
  const retryButton = useRef<HTMLButtonElement>(null);
  const activeRequestId = useRef(0);
  const cancelActiveLoad = useRef<(() => void) | undefined>(undefined);

  /**
   * Loads applications within a fixed deadline, ignores stale completions, and
   * restores search focus only when a user-initiated retry succeeds.
   */
  const loadApps = useCallback(async (restoreFocus: boolean) => {
    activeRequestId.current += 1;
    const requestId = activeRequestId.current;
    cancelActiveLoad.current?.();
    setLoading(true);
    setLoadError(false);

    const abortController = new AbortController();
    let rejectOnAbort: (() => void) | undefined;
    const deadline = new Promise<never>((_resolve, reject) => {
      /** Settles the local race whenever timeout, retry, or unmount cancels this load. */
      rejectOnAbort = () => {
        reject(new Error('Application list request timed out or was cancelled.'));
      };

      abortController.signal.addEventListener('abort', rejectOnAbort, {once: true});
    });

    /** Aborts the worker request and settles the already-observed local deadline. */
    const cancelRequest = () => {
      abortController.abort(new Error('Application list request timed out or was cancelled.'));
    };

    const timeoutId = globalThis.setTimeout(cancelRequest, appLoadTimeoutMs);
    cancelActiveLoad.current = cancelRequest;

    /** Captures synchronous container access failures in a promise that the race can observe. */
    const requestApps = async () => {
      // The frontend-types submodule has not yet adopted the runtime client's cancellation option.
      const appClient = getContainer().applicationClient.application as AbortableAppClient;
      return appClient.getApplications(abortController.signal);
    };

    const appRequest = requestApps();

    try {
      const availableApps: unknown = await Promise.race([
        appRequest,
        deadline,
      ]);
      if (activeRequestId.current !== requestId) {
        return;
      }

      // The legacy frontend-types contract says string, but the runtime client returns this object array.
      setApps(availableApps as QepComponent[]);
      if (restoreFocus) {
        searchInput.current?.focus();
      }
    } catch {
      if (activeRequestId.current === requestId) {
        setLoadError(true);
      }
    } finally {
      globalThis.clearTimeout(timeoutId);
      if (rejectOnAbort) {
        abortController.signal.removeEventListener('abort', rejectOnAbort);
      }

      if (cancelActiveLoad.current === cancelRequest) {
        cancelActiveLoad.current = undefined;
      }

      if (activeRequestId.current === requestId) {
        setLoading(false);
      }
    }
  }, []);

  /** Starts the initial load and cancels its worker request when the selector unmounts. */
  useEffect(() => {
    void loadApps(false);

    /** Invalidates the request and settles its deadline before unmounting. */
    return () => {
      activeRequestId.current += 1;
      cancelActiveLoad.current?.();
      cancelActiveLoad.current = undefined;
    };
  }, [loadApps]);

  /** Restores focus to Retry after either the initial load or another retry fails. */
  useEffect(() => {
    if (loadError && !loading) {
      retryButton.current?.focus();
    }
  }, [loadError, loading]);

  /** Retries loading applications and restores search focus only after success. */
  const handleRetry = useCallback(() => {
    void loadApps(true);
  }, [loadApps]);

  const visibleApps = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return apps
      .filter(component => component.enabled)
      .filter(component => !normalized || component.name.toLowerCase().includes(normalized))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [apps, query]);

  /** Builds a click handler that replaces the selector with an application. */
  function onClick(component: QepComponent) {
    return () => {
      if (!model.current || !layout.current) {
        return;
      }

      const node = model.current.getNodeById(tabId);
      const tabSetId = node?.getParent()?.getId();

      component.configs.linkerGroups = ['group1'];
      if (tabSetId) {
        layout.current.addTabToTabSet(tabSetId, toTabNode(component));
      } else {
        layout.current.addTabToActiveTabSet(toTabNode(component));
      }

      model.current.doAction(Actions.deleteTab(tabId));
    };
  }

  return (
    <div className='browser-component-selector'>
      <div className='browser-component-selector-header'>
        <h1 className='browser-component-selector-title'>Open application</h1>
        <p className='browser-component-selector-subtitle'>
          Choose an application to open in this tab.
        </p>
        <div className='browser-component-selector-search'>
          <svg
            className='browser-component-selector-search-icon'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <circle cx='11' cy='11' r='7' />
            <line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
          <input
            ref={searchInput}
            type='text'
            className='browser-component-selector-search-input'
            placeholder='Search applications'
            value={query}
            onChange={event => {
              setQuery(event.target.value);
            }}
            autoFocus
          />
        </div>
      </div>

      {renderBody({
        visibleApps,
        totalApps: apps.filter(component => component.enabled).length,
        loading,
        loadError,
        onRetry: handleRetry,
        onClick,
        retryButton,
      })}
    </div>
  );
}

type BodyProps = {
  visibleApps: QepComponent[];
  totalApps: number;
  loading: boolean;
  loadError: boolean;
  onRetry: () => void;
  onClick: (component: QepComponent) => () => void;
  retryButton: React.RefObject<HTMLButtonElement>;
};

/** Renders loading, failure, empty, or application-list content. */
function renderBody({
  visibleApps,
  totalApps,
  loading,
  loadError,
  onRetry,
  onClick,
  retryButton,
}: BodyProps): React.ReactNode {
  if (loading) {
    return (
      <div className='browser-component-selector-empty' role='status'>
        Loading applications…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className='browser-component-selector-empty' role='alert'>
        <p>Unable to load applications.</p>
        <button
          ref={retryButton}
          type='button'
          className='browser-component-selector-retry'
          onClick={onRetry}
        >
          Retry
        </button>
      </div>
    );
  }

  if (visibleApps.length === 0) {
    const message = totalApps === 0
      ? 'No applications are available.'
      : 'No applications match your search.';
    return <div className='browser-component-selector-empty'>{message}</div>;
  }

  return (
    <ul className='browser-component-selector-list'>
      {visibleApps.map(component => (
        <li key={component.id} className='browser-component-selector-list-item'>
          <button
            type='button'
            className='browser-component-selector-card'
            onClick={onClick(component)}
            title={component.name}
          >
            <span
              className='browser-component-selector-icon'
              style={{backgroundColor: getAccent(component.name)}}
              aria-hidden='true'
            >
              {getInitials(component.name)}
            </span>
            <span className='browser-component-selector-name'>{component.name}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
